import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Copy, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getPageHeader } from '@/constants'
import { useAdminBasePath, adminPath } from '@/components/admin/use-admin-base-path'
import {
  useCreateRubricCorpus,
  useDuplicateRubricCorpus,
  useRubricCorpora,
  useRubricMethodologies,
  type RubricCorpus,
} from '@/hooks/use-evaluation-rubrics-admin'

export function RubricCorporaPage() {
  const basePath = useAdminBasePath()
  const navigate = useNavigate()
  const { data: corpora, isLoading, error } = useRubricCorpora()
  const { data: methodologies } = useRubricMethodologies()
  const createCorpus = useCreateRubricCorpus()
  const duplicateCorpus = useDuplicateRubricCorpus()
  const [createOpen, setCreateOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [methodologyId, setMethodologyId] = useState('employment_match')
  const [slugSuffix, setSlugSuffix] = useState('custom')

  const methodologyLabel = useMemo(() => {
    const map = new Map((methodologies ?? []).map((m) => [m.methodology_id, m.label]))
    return (id: string) => map.get(id) ?? id
  }, [methodologies])

  useEffect(() => {
    const first = methodologies?.[0]?.methodology_id
    if (first && !methodologies?.some((m) => m.methodology_id === methodologyId)) {
      setMethodologyId(first)
    }
  }, [methodologies, methodologyId])

  const header = getPageHeader(
    adminPath(basePath, 'evaluation-rubrics'),
    'Rubrics',
    'Create and edit evaluation rubrics that power Analysis and other assessments.',
  )

  async function handleDuplicate(event: MouseEvent, corpus: RubricCorpus) {
    event.preventDefault()
    event.stopPropagation()
    try {
      const copy = await duplicateCorpus.mutateAsync({
        corpusId: corpus.id,
        label: `${corpus.label} (copy)`,
      })
      toast.success(`Duplicated ${corpus.label}`)
      navigate(adminPath(basePath, `evaluation-rubrics/${copy.id}`))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not duplicate rubric')
    }
  }

  async function handleCreate() {
    if (!label.trim()) {
      toast.error('Give this rubric a name')
      return
    }
    try {
      const created = await createCorpus.mutateAsync({
        methodology_id: methodologyId,
        label: label.trim(),
        slug_suffix: slugSuffix.trim() || 'custom',
      })
      toast.success(`Created ${created.label}`)
      setCreateOpen(false)
      navigate(adminPath(basePath, `evaluation-rubrics/${created.id}`))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create rubric')
    }
  }

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        leading={<PageHeaderLeading />}
        title={header.title}
        description={header.description}
        actions={
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1" aria-hidden />
            New rubric
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading rubrics…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load rubrics'}
        </p>
      ) : !corpora?.length ? (
        <Card>
          <CardContent className="py-10 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              No rubrics yet. Create one from a methodology template to get started.
            </p>
            <Button type="button" onClick={() => setCreateOpen(true)}>
              New rubric
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {corpora.map((corpus) => (
            <Link
              key={corpus.id}
              to={adminPath(basePath, `evaluation-rubrics/${corpus.id}`)}
              className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="h-full transition-colors hover:bg-muted/30">
                <CardHeader className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{corpus.status}</Badge>
                    {corpus.current_revision ? (
                      <Badge>v{corpus.current_revision.version}</Badge>
                    ) : null}
                  </div>
                  <CardTitle className="text-base">{corpus.label}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>{methodologyLabel(corpus.methodology_id)}</p>
                  <p className="font-mono text-xs">{corpus.slug}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(event) => void handleDuplicate(event, corpus)}
                    disabled={duplicateCorpus.isPending}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" aria-hidden />
                    Duplicate
                  </Button>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New rubric</DialogTitle>
            <DialogDescription>
              Pick a methodology template. You can edit criteria after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rubric-label">Name</Label>
              <Input
                id="rubric-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Leadership interview rubric"
              />
            </div>
            <div className="space-y-2">
              <Label>Methodology</Label>
              <Select
                value={methodologyId}
                onValueChange={(value) => {
                  setMethodologyId(value)
                  const match = methodologies?.find((m) => m.methodology_id === value)
                  if (match && !label.trim()) setLabel(match.label)
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(methodologies ?? []).map((m) => (
                    <SelectItem key={m.methodology_id} value={m.methodology_id}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {(methodologies ?? []).find((m) => m.methodology_id === methodologyId)?.description}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug-suffix">Slug suffix</Label>
              <Input
                id="slug-suffix"
                value={slugSuffix}
                onChange={(e) => setSlugSuffix(e.target.value)}
                className="font-mono text-xs"
                placeholder="custom"
              />
              <p className="text-xs text-muted-foreground">
                Saved as {methodologyId}-{slugSuffix || 'custom'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleCreate()} disabled={createCorpus.isPending}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
